import { BulkOperationParams } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils';
import { createBulkActions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/createBulkActions.ts';
import { CreateSecurityDrawer } from '@dealstack/frontend/shared/components/CreateSecurityDrawer/CreateSecurityDrawer';
import { EditSecurityDrawer } from '@dealstack/frontend/shared/components/EditSecurityDrawer/EditSecurityDrawer';
import { useCreateNewSecurityDrawerController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/hooks/useCreateNewSecurityDrawerController.ts';
import { useEntityNomineeStructure } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/hooks/useEntityNomineeStructure.ts';
import { useSecurityBulkDelete } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/hooks/useSecurityBulkDelete.ts';
import { useSecurityBulkExport } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/hooks/useSecurityBulkExport.ts';
import { UpdateSecurityCurrencyDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/UpdateSecurityCurrencyDrawer/UpdateSecurityCurrencyDrawer.tsx';
import { useSecuritiesController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/useSecuritiesController';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { DateSelectionMenu } from '@dealstack/frontend/app/pages/Project/Cap/common/DateSelectMenu/dateSelectMenu';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { OneSchemaImporterModal } from '@dealstack/frontend/shared/components/OneSchema/OneSchemaImporterModal';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import {
	getTableAllSelected,
	getTableSelection,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useTableColPreferences } from '@dealstack/frontend/shared/hooks/useTableColPreferences/useTableColPreferences.hook.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OneSchemaTemplateKey } from '@dealstack/shared/lib/frontend/common-ui/oneSchemaTemplateKey';
import {
	CurrencyType,
	GetPersonSecurityDataRes,
	PersonType,
	SecurityFilterDto,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import { Button, Flex, Text } from '@mantine/core';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { generatePath, Link, useNavigate } from 'react-router-dom';

interface Props {
	selectedEntityLoading?: boolean;
	dealCurrency?: CurrencyType;
}

const Securities = ({ dealCurrency }: Props) => {
	const tCapManage = useTranslationCapManage();
	const navigate = useNavigate();
	const { columnVisibility, handleColumnVisibilityChange } = useTableColPreferences({
		storageKey: StorageKeys.CAP_SECURITIES_TABLE_COLS,
		defaultColumnVisibility: {
			id: false,
			createdAt: false,
			authorisedCapital: false,
			name: true,
			type: true,
			currency: true,
			nominalValue: true,
			votesPerUnit: true,
			totalNominalValue: true,
			totalSharePremium: true,
			totalShareCapital: true,
			totalVested: true,
			totalUnvested: true,
		},
	});

	const {
		tableRef,
		onBulkUploadSecurities,
		oneSchemaConfig,
		getSecuritiesRequest,
		securitiesRefetch,
		mantineTableDataOperations,
		columns,
		showOneSchemaModal,
		setShowOneSchemaModal,
		showCreateNewSecurityDrawer,
		setShowCreateNewSecurityDrawer,
		showUpdateCurrencyDrawer,
		setShowUpdateCurrencyDrawer,
		securityToEdit,
		setSecurityToEdit,
		setShowEditSecurityDrawer,
		showEditSecurityDrawer,
		onCreateSecuritiesModalClose,
		isTableLoading,
		isLoading: isSecuritiesLoading,
		selectedEntityId,
		showTable,
		deleteSecurityRequest,
		isTsaExist,
		fetchTsaData,
		userIsAdmin,
		refetchSelectedEntity,
		selectedEntityData,
		hasEditorPermission,
		hasExportViewerPermission,
		dealId,
		dataOperationsRef,
		filterDate,
		setFilterDate,
	} = useSecuritiesController(true);

	const entityCurrency = getSecuritiesRequest.data?.securities?.[0]?.currency;

	const { createSecurityRequest, onCreate: onCreateSecurity } =
		useCreateNewSecurityDrawerController(securitiesRefetch, onCreateSecuritiesModalClose);

	const handleCheckExistingSecurityRefetch = () => {
		if (selectedEntityId) {
			void refetchSelectedEntity();
		}
	};

	const { downloadExcel, isLoading: isCSVDownloadLoading } = useSecurityBulkExport();

	// CAP-1974: entity-level nominee flags are only settable when the deal itself is flagged as a
	// nominee structure (Deal Settings > Nominee structure), so the toggle is gated on the deal flag.
	// Only a legal person can carry the flag — it is written through the legal-person endpoint.
	const { deal } = useContext(DealContext);
	// CAP-1062: the feature flag keeps every nominee surface dark, including this toggle.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	const showNomineeStructureToggle =
		nomineeStructuresEnabled &&
		!!deal?.hasNomineeStructure &&
		selectedEntityData?.type === PersonType.LEGAL;

	const { setHasNomineeStructure, isSaving: isSavingNomineeStructure } =
		useEntityNomineeStructure({
			personId: selectedEntityId,
			refetchSelectedEntity,
			refetchSecurities: securitiesRefetch,
		});

	const {
		deleteSecurities,
		deleteSecuritiesSuccess,
		isLoading: isDeleteLoading,
	} = useSecurityBulkDelete(securitiesRefetch, handleCheckExistingSecurityRefetch);

	const isLoading = isSecuritiesLoading || isCSVDownloadLoading || isDeleteLoading;

	const [confirmBulkDeleteOpened, setConfirmBulkDeleteOpened] = useState<boolean>(false);
	const [bulkDeletePayload, setBulkDeletePayload] =
		useState<
			BulkOperationParams<GetPersonSecurityDataRes, TableOperations<SecurityFilterDto>>
		>();

	const handleConfirmBulkDelete = async () => {
		if (bulkDeletePayload) {
			await deleteSecurities(bulkDeletePayload);
			securitiesRefetch();
		}
	};

	const handleBulkDelete = async (
		payload: BulkOperationParams<GetPersonSecurityDataRes, TableOperations<SecurityFilterDto>>
	) => {
		setBulkDeletePayload(payload);

		const selection = getTableSelection(payload.table);
		const ids = selection.map((row) => row.id);
		await fetchTsaData(ids);

		setConfirmBulkDeleteOpened(true);
	};

	useEffect(() => {
		if (deleteSecurityRequest.isSuccess) {
			setSecurityToEdit(undefined);
		}
	}, [deleteSecurityRequest.isSuccess, setSecurityToEdit]);

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.SECURITIES]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityData?.displayName,
				noLink: true,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityData]
	);

	useBreadcrumbs(breadcrumbs);

	return (
		<Flex direction="column" p="1rem" gap="1rem" h="100%">
			<Flex justify="space-between" align="center" gap="1rem">
				<DateSelectionMenu dateTo={filterDate} setFilterDate={setFilterDate} />
				{hasEditorPermission && (
					<Flex gap="0.5rem" align="center">
						{showNomineeStructureToggle && (
							<AdvancedSwitch
								selectedValue={!!selectedEntityData?.hasNomineeStructure}
								disabled={isSavingNomineeStructure}
								onChange={(event) => setHasNomineeStructure(!!event)}
								// component="span": AdvancedSwitch wraps leftText in a Text, which renders
								// a <p>, so a nested Text would nest <p> inside <p>.
								leftText={
									<Text
										size="sm"
										component="span"
										style={{ whiteSpace: 'nowrap' }}
									>
										{tCapManage('securities.header_actions.nominee_structures')}
									</Text>
								}
								unselectedLetterSpacing="0"
								data-testid={TEST_ELEMENT_IDS.CAP_ENTITY_NOMINEE_STRUCTURE_TOGGLE}
							/>
						)}
						<Link to={`..${PEOPLE_ROUTES.SECURITY_RANKING}`}>
							<Button
								data-testid={TEST_ELEMENT_IDS.CAP_ADD_MANAGE_RANKING_BUTTON}
								variant="default"
								leftSection={<Icon variant="ranking" size="lg" />}
							>
								{tCapManage('securities.header_actions.manage_ranking')}
							</Button>
						</Link>
						<Button
							onClick={() => {
								setShowOneSchemaModal(true);
							}}
							variant="default"
							leftSection={<Icon variant="upload-v3-thin" size="lg" />}
						>
							{tCapManage('securities.header_actions.upload_security')}
						</Button>
						<Button
							onClick={() => {
								setShowCreateNewSecurityDrawer(true);
							}}
							data-testid={TEST_ELEMENT_IDS.CAP_ADD_SECURITY_BUTTON}
							leftSection={<Icon variant="add" size="lg" />}
						>
							{tCapManage('securities.header_actions.add_security')}
						</Button>
					</Flex>
				)}
			</Flex>
			{showTable ? (
				<MantineTable<GetPersonSecurityDataRes>
					getRowId={(row) => row.id}
					enableColumnResizing={true}
					pinnedColumnIds={['id', 'name']}
					renderBulkActionsMenu
					actions={(table) =>
						createBulkActions(table, {
							isAllSelected: getTableAllSelected(table),
							isLoading,
							hasExportViewerPermission,
							hasEditorPermission,
							userIsAdmin,
							selectedEntityId,
							tCapManage,
							downloadExcel,
							handleBulkDelete,
							dataOperationsRef,
							showUpdateCurrencyDrawerButton:
								!!getSecuritiesRequest.data?.securities?.length,
							setShowUpdateCurrencyDrawer,
							filterDate,
						})
					}
					mantineTableBodyRowProps={({ row }) => ({
						id: row.id,
						style: {
							cursor: 'pointer',
						},
						onClick: () => {
							const path = generatePath(`..${PEOPLE_ROUTES.SECURITY_DETAILS}`, {
								securityId: row.original.id,
							});

							navigate(path, {
								state: {
									navigationSource: true,
								},
							});
						},
					})}
					tableRef={tableRef}
					columns={columns}
					isLoading={isTableLoading}
					dataOperations={mantineTableDataOperations}
					data={getSecuritiesRequest.data?.securities || []}
					initialState={{
						columnVisibility,
					}}
					state={{
						columnVisibility,
					}}
					onColumnVisibilityChange={handleColumnVisibilityChange}
					rowCount={getSecuritiesRequest.data?.totalCount || 0}
					enableRowActions={false}
					enablePagination={true}
					manualPagination={true}
					enableStickyHeader={true}
					enableStickyFooter={true}
				/>
			) : (
				<EmptyState
					title={tCapManage('securities.initial_page.title')}
					description={tCapManage('securities.initial_page.description')}
					icon="es-box-magnifying-glass"
				>
					{hasEditorPermission && (
						<Flex align="center" gap="0.5rem">
							<Button
								onClick={() => setShowOneSchemaModal(true)}
								variant="default"
								leftSection={<Icon variant="upload-v3-thin" size="lg" />}
							>
								{tCapManage('securities.initial_page.upload_security')}
							</Button>
							<Button
								onClick={() => setShowCreateNewSecurityDrawer(true)}
								leftSection={<Icon variant="add" size="lg" />}
							>
								{tCapManage('securities.initial_page.add_security')}
							</Button>
						</Flex>
					)}
				</EmptyState>
			)}
			<UpdateSecurityCurrencyDrawer
				opened={showUpdateCurrencyDrawer}
				onClose={() => setShowUpdateCurrencyDrawer(false)}
				refreshSecuritiesList={securitiesRefetch}
				currentCurrency={entityCurrency}
				personName={selectedEntityData?.displayName || ''}
			/>
			<CreateSecurityDrawer
				opened={showCreateNewSecurityDrawer}
				onClose={onCreateSecuritiesModalClose}
				onCreate={onCreateSecurity}
				isLoading={createSecurityRequest.isPending}
				defaultCurrency={entityCurrency ?? dealCurrency}
				lockCurrency={!!entityCurrency}
			/>
			<EditSecurityDrawer
				opened={showEditSecurityDrawer}
				onClose={() => {
					setSecurityToEdit(undefined);
					setShowEditSecurityDrawer(false);
				}}
				onSaved={securitiesRefetch}
				security={securityToEdit}
				dealId={dealId ?? ''}
				canEdit={hasEditorPermission}
			/>
			<ConfirmDeleteDrawer
				opened={confirmBulkDeleteOpened}
				onClose={() => setConfirmBulkDeleteOpened(false)}
				title={tCapManage('delete.security.drawer.title')}
				alertTitle={
					isTsaExist
						? tCapManage('delete.security.drawer.bulk.forbiddenTitle')
						: tCapManage('delete.security.drawer.bulk.alertTitle')
				}
				alertDescription={
					isTsaExist
						? tCapManage('delete.security.drawer.bulk.forbiddenDescription')
						: tCapManage('delete.security.drawer.bulk.alertDescription')
				}
				hideAlertIcon={true}
				inputLabel={tCapManage('delete.security.drawer.bulk.inputLabel')}
				inputPlaceholder={tCapManage('delete.security.drawer.bulk.inputPlaceholder')}
				confirmDeleteText={tCapManage('delete.security.drawer.bulk.confirmDeleteText')}
				wordMismatchErrorMessage={tCapManage(
					'delete.security.drawer.bulk.wordMismatchError'
				)}
				deleteBtnText={tCapManage('delete.security.drawer.confirmDelete')}
				cancelBtnText={tCapManage('delete.security.drawer.cancel')}
				deleteSuccess={deleteSecuritiesSuccess}
				deleteLoading={isLoading}
				handleDelete={handleConfirmBulkDelete}
			/>
			<OneSchemaImporterModal
				isOpen={showOneSchemaModal}
				onRequestClose={() => setShowOneSchemaModal(false)}
				onSuccess={onBulkUploadSecurities}
				clientId={oneSchemaConfig?.oneSchemaClientId as string}
				userJwt={oneSchemaConfig?.oneSchemaJwt as string}
				templateKey={OneSchemaTemplateKey.CAP_SECURITIES_UPLOAD}
				devMode={process.env.NODE_ENV !== 'production'}
				importConfig={{ type: 'local' }}
			/>
		</Flex>
	);
};
const _SecuritiesExport = memo(Securities);

export { _SecuritiesExport as Securities };
